import React, { useCallback, useEffect, useState } from 'react';
import client from '../api/client';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

function formatDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('de-DE');
}

function formatHours(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  const totalMinutes = Math.round(numeric * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} h`;
}

function formatEntrySummary(payload = {}) {
  const summary = payload.entrySummary || {};
  const parts = [
    formatDate(summary.date),
    summary.entryType || null,
    formatHours(summary.hours),
    summary.responsibleName ? `Verantwortlich: ${summary.responsibleName}` : null,
  ].filter(Boolean);

  if (!parts.length) {
    return 'Ein Logbucheintrag';
  }

  return parts.join(' · ');
}

function getRequestTitle(request) {
  if (request.action_type === 'create') {
    return `${request.requested_by_name} möchte einen Eintrag für dein Logbuch anlegen`;
  }

  if (request.action_type === 'delete') {
    return `${request.requested_by_name} möchte die Löschung eines Logbucheintrags beantragen`;
  }

  return `${request.requested_by_name} möchte eine Änderung an deinem Logbucheintrag beantragen`;
}

function formatRequestSummary(request) {
  const payload = request.payload || {};
  if (request.action_type === 'create') {
    return `Neuer Eintrag: ${formatEntrySummary(payload)}`;
  }
  if (request.action_type === 'update') {
    return `Beantragte Änderung: ${formatEntrySummary(payload)}`;
  }
  if (request.action_type === 'delete') {
    return `Beantragte Löschung: ${formatEntrySummary(payload)}`;
  }
  return 'Änderungsanfrage';
}

function getRequestReason(request) {
  const reason = typeof request?.reason === 'string' ? request.reason.trim() : '';
  return reason;
}

function formatFullEditDetails(payload = {}) {
  if (typeof payload.entryType !== 'string' || typeof payload.date !== 'string') {
    return [];
  }

  const source = payload.sourceEntry || {};
  const rows = [
    ['Datum', formatDate(source.date), formatDate(payload.date)],
    ['Tätigkeit', source.entryType, payload.entryType],
    ['Variante', source.entryVariant, payload.entryVariant],
    ['Verantwortlich', source.responsibleName, payload.responsibleDisplayName || payload.entrySummary?.responsibleName],
    ['Lehrhöhenretterstunden', source.responsibleIsLehrhSnapshot ? 'Ja' : 'Nein', payload.responsibleIsLehrhSnapshot ? 'Ja' : 'Nein'],
    ['Beschreibung', source.description, payload.description],
    ['Höhenretterstunden', formatHours(source.hours), formatHours(payload.hours)],
    ['Zeitraum', source.timeFrom && source.timeTo ? `${source.timeFrom} - ${source.timeTo}` : '-', payload.timeFrom && payload.timeTo ? `${payload.timeFrom} - ${payload.timeTo}` : '-'],
    ['Pause', source.pauseTaken ? formatHours(source.pauseHours) : 'Nein', payload.pauseTaken ? formatHours(payload.pauseHours) : 'Nein'],
    ['Schlagwörter', Array.isArray(source.keywords) ? source.keywords.join(', ') : '', Array.isArray(payload.keywords) ? payload.keywords.join(', ') : ''],
  ];

  return rows.filter(([, previous, proposed]) => String(previous || '-') !== String(proposed || '-'));
}

function formatCreateDetails(payload = {}) {
  if (typeof payload.entryType !== 'string' || typeof payload.date !== 'string') return [];
  return [
    ['Datum', formatDate(payload.date)],
    ['Tätigkeit', payload.entryType],
    ['Variante', payload.entryVariant],
    ['Verantwortlich', payload.responsibleDisplayName || payload.entrySummary?.responsibleName],
    ['Lehrhöhenretterstunden', payload.responsibleIsLehrhSnapshot ? 'Ja' : 'Nein'],
    ['Beschreibung', payload.description],
    ['Höhenretterstunden', formatHours(payload.hours)],
    ['Zeitraum', payload.timeFrom && payload.timeTo ? `${payload.timeFrom} - ${payload.timeTo}` : '-'],
    ['Pause', payload.pauseTaken ? formatHours(payload.pauseHours) : 'Nein'],
    ['Schlagwörter', Array.isArray(payload.keywords) ? payload.keywords.join(', ') : ''],
  ];
}

function ChangeRequestInbox({
  onResolved,
  embedded = false,
  showWhenEmpty = false,
  title = 'Offene Änderungsanfragen',
  onCountChange,
}) {
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [searchParams] = useSearchParams();
  const requestedFocusId = Number(searchParams.get('request') || 0);

  const loadInbox = useCallback(async () => {
    try {
      setError('');
      const response = await client.get('/api/change-requests/inbox');
      const nextRequests = response.data.requests || [];
      setRequests(nextRequests);
      if (onCountChange) {
        onCountChange(nextRequests.length);
      }
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Anfragen konnten nicht geladen werden.');
    }
  }, [onCountChange]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!requestedFocusId || !requests.length) return;
    const found = requests.some((request) => Number(request.id) === Number(requestedFocusId));
    if (!found) return;

    const timer = setTimeout(() => {
      const targetElement = document.getElementById(`change-request-${requestedFocusId}`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);

    return () => clearTimeout(timer);
  }, [requestedFocusId, requests]);

  const handleDecision = async (request, decision) => {
    try {
      setBusyId(request.id);
      await client.post(`/api/change-requests/${request.id}/${decision}`, { requestRevision: request.requestRevision });
      await loadInbox();
      if (onResolved) onResolved();
    } catch (decisionError) {
      setError(decisionError.response?.data?.error || 'Entscheidung konnte nicht gespeichert werden.');
    } finally {
      setBusyId(null);
    }
  };

  if (!requests.length && !error && !showWhenEmpty) {
    return null;
  }

  const content = (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip size="small" color={requests.length > 0 ? 'warning' : 'success'} label={requests.length > 0 ? `${requests.length} offen` : 'Keine offenen Anfragen'} />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!error && requests.length === 0 && showWhenEmpty ? <Alert severity="success">Zurzeit liegen keine offenen Änderungsanfragen vor.</Alert> : null}

      <Stack spacing={1.25}>
        {requests.map((request) => {
          const fullEditDetails = request.action_type === 'update' ? formatFullEditDetails(request.payload) : [];
          const createDetails = request.action_type === 'create' ? formatCreateDetails(request.payload) : [];
          return (
          <Card
            key={request.id}
            id={`change-request-${request.id}`}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: Number(request.id) === Number(requestedFocusId) ? 'primary.main' : 'divider',
              backgroundColor: Number(request.id) === Number(requestedFocusId) ? 'action.hover' : 'transparent',
            }}
          >
            <CardContent sx={{ py: 1.5, px: { xs: 1.25, sm: 2 } }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {getRequestTitle(request)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatRequestSummary(request)}
                </Typography>
                {fullEditDetails.length > 0 && (
                  <Stack spacing={0.75} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                    {fullEditDetails.map(([label, previous, proposed]) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography variant="body2">{`Bisher: ${previous || '-'}`}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{`Beantragt: ${proposed || '-'}`}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
                {createDetails.length > 0 && (
                  <Stack spacing={0.75} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                    {createDetails.map(([label, proposed]) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{proposed || '-'}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
                {getRequestReason(request) && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    <Typography variant="body2">
                      <strong>Begründung:</strong> {getRequestReason(request)}
                    </Typography>
                  </Alert>
                )}
                <Divider />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleDecision(request, 'approve')}
                    disabled={busyId === request.id}
                    sx={{ minHeight: { xs: 42, sm: 'auto' } }}
                  >
                    Genehmigen
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleDecision(request, 'reject')}
                    disabled={busyId === request.id}
                    sx={{ minHeight: { xs: 42, sm: 'auto' } }}
                  >
                    Ablehnen
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
          );
        })}
      </Stack>
    </Stack>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        {content}
      </CardContent>
    </Card>
  );
}

export default ChangeRequestInbox;
